import { useTranslation } from 'react-i18next';

/**
 * Fallback renderer for a block with no dedicated component yet.
 *
 * It renders a flat key/value list rather than nothing at all: showing the raw
 * structure is more useful to a user than an empty card, and it makes it obvious
 * to a developer which block still needs a proper renderer.
 */
export function GenericBlock({ data }: { data: unknown }): React.ReactNode {
  const { t } = useTranslation();

  const entries = flatten(data);
  if (entries.length === 0) {
    return <p className="text-sm text-[var(--color-muted-fg)]">{t('blocks.emptyNoted', 'Nothing on record.')}</p>;
  }

  return (
    <dl className="space-y-1.5 text-sm">
      {entries.map(([key, value]) => (
        <div key={key} className="flex gap-3">
          <dt className="w-40 shrink-0 text-[var(--color-muted-fg)]">{humanize(key)}</dt>
          <dd className="min-w-0 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Flatten one level of an object into readable pairs. */
function flatten(data: unknown, prefix = ''): [string, string][] {
  if (data === null || data === undefined) return [];
  if (typeof data !== 'object') return [[prefix || 'value', String(data)]];
  if (Array.isArray(data)) {
    return data.length === 0 ? [] : [[prefix || 'items', `${data.length}`]];
  }

  const out: [string, string][] = [];
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      out.push(...flatten(value, path));
    } else if (Array.isArray(value)) {
      out.push([path, `${value.length}`]);
    } else if (value !== null && value !== undefined) {
      out.push([path, String(value)]);
    }
  }
  return out;
}

function humanize(key: string): string {
  const last = key.split('.').pop() ?? key;
  return last.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}
